import React, { FC, useEffect, useState } from 'react';
import { Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ColumnFlexContainer,
  ContainerV2,
  RowFlexContainer,
} from '../../../stories/atoms/containerV2/ContainerV2';
import { useThemeContext } from '../../../global-state/themeContext';
import { Animation, Animations, IconWithText } from '../../../stories/molecules';
import { Icons, Typography } from '../../../stories/atoms';
import { AccessRequestDetails, AccessRequestItem, useUserPermissions } from '../../../hooks/useUserPermissions';
import { convertCamelCaseToTitleCase, isFailureResponse } from '../../../utils';
import { StatusTableitem } from '../../accessApproval/statusTableItem/StatusTableItem';
import { accessesRequestedTranslations } from '../../../translations';
import { useAsyncAction } from '../../../hooks/useAsyncAction';
import { ViewRequestModal } from './ViewRequestModal';

type AccessesRequestedProps = {
  isMobile: boolean;
  refreshKey?: number;
};

export const AccessesRequested: FC<AccessesRequestedProps> = ({ isMobile, refreshKey }) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const { fetchRequestedAssignments } = useUserPermissions();

  const [selectedRequest, setSelectedRequest] = useState<AccessRequestItem | null>(null);
  const [showModal, setShowModal] = useState(false);

  const { title, subtitle, loading, error, noRequests, viewRequest } = accessesRequestedTranslations;

  const {
    isLoading: isFetching,
    isError: hasError,
    result: requestedAssignments,
    triggerAction: triggerFetch,
  } = useAsyncAction<AccessRequestDetails>();

  const fetchData = async () => {
    const response = await fetchRequestedAssignments();

    if (isFailureResponse(response)) {
      throw new Error();
    }
    return response.data;
  };

  useEffect(() => {
    triggerFetch(fetchData);
  }, [refreshKey]);

  const requests = requestedAssignments?.requests ?? [];

  const handleViewRequest = (request: AccessRequestItem) => {
    setSelectedRequest(request);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
  };

  const sortedRequests = React.useMemo(() => {
    return [...requests].sort(
      (a, b) => new Date(b.requestTimestamp).getTime() - new Date(a.requestTimestamp).getTime(),
    );
  }, [requests]);

  const isNoRequests = sortedRequests.length === 0;

  const handleReapplySuccess = () => {
    triggerFetch(fetchData);
  };

  return (
    <>
      {showModal && selectedRequest && (
        <ViewRequestModal
          request={selectedRequest}
          onClose={handleCloseModal}
          onReapplySuccess={handleReapplySuccess}
        />
      )}
      <ColumnFlexContainer
        data-testid="accesses-requested-section"
        backgroundColor={mode === 'dark' ? 'backgroundPaperDark' : 'white'}
        hasBorder
        borderRadius={[2]}
        width="100%"
      >
        <ContainerV2
          backgroundColor={mode === 'dark' ? 'backgroundSelectedHover' : 'backgroundTableHeader'}
          padding={isMobile ? [3, 4] : [4, 8]}
          borderRadius={[4, 4, 0, 0]}
        >
          <IconWithText
            label={t(title.key, title.defaultValue)}
            iconOptions={{ icon: Icons.HOURLASS, size: 'small' }}
            textOptions={{ type: 'h4' }}
          />
        </ContainerV2>
        <ContainerV2 padding={isMobile ? [4, 4] : [4, 8]}>
          <Typography>{t(subtitle.key, subtitle.defaultValue)}</Typography>
          {isFetching && (
            <Animation
              title=""
              animation={Animations.LOADING}
              subtitle={t(loading.key, loading.defaultValue)}
              size="small"
            />
          )}
          {hasError && (
            <Animation
              title=""
              animation={Animations.SAD}
              subtitle={t(error.key, error.defaultValue)}
              size="small"
            />
          )}
          {isNoRequests && !isFetching && !hasError && (
            <Animation title="" subtitle={t(noRequests.key, noRequests.defaultValue)} size="small" />
          )}
          {!isFetching && !hasError && !isNoRequests && (
            <ColumnFlexContainer
              hasBorder
              borderColor="hoverGray"
              borderRadius={[2]}
              margin={[4, 0]}
              maxHeight="250px"
              overflow="auto"
            >
              {sortedRequests.map((request: AccessRequestItem, index: number) => (
                <React.Fragment key={request.id}>
                  <RowFlexContainer justifyContent="between" padding={[4]}>
                    <ColumnFlexContainer>
                      <Typography>
                        {request.requestedAssignment.title
                          ? convertCamelCaseToTitleCase(request.requestedAssignment.title)
                          : ''}
                      </Typography>
                      <RowFlexContainer gap={[4]}>
                        <StatusTableitem accessRequestItem={request} position="center" flexType="row" />
                      </RowFlexContainer>
                    </ColumnFlexContainer>
                    <IconWithText
                      label={t(viewRequest.key, viewRequest.defaultValue)}
                      labelPosition="left"
                      iconOptions={{ icon: Icons.ARROW, size: 'small' }}
                      onClick={() => handleViewRequest(request)}
                    />
                  </RowFlexContainer>
                  {index < sortedRequests.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </ColumnFlexContainer>
          )}
        </ContainerV2>
      </ColumnFlexContainer>
    </>
  );
};
